import { supabase } from './supabaseClient';

// Helper function to ensure valid session
export const ensureValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Session error:', error);
      return null;
    }

    if (!session) {
      console.log('No active session found');
      return null;
    }

    console.log('Valid session found for:', session.user.email);
    return session;
  } catch (error) {
    console.error('Error ensuring valid session:', error);
    return null;
  }
};

export const getCurrentUser = async () => {
  try {
    const session = await ensureValidSession();
    
    if (!session) {
      console.log('No valid session available');
      return null;
    }

    console.log('Active session found for:', session.user.email);

    // Fetch profile data from database
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError);
    }

    // Combine session and profile data
    const userData = {
      userId: session.user.id,
      email: profile?.email || session.user.email,
      full_name: profile?.full_name || session.user.user_metadata?.full_name || session.user.user_metadata?.name || '',
      date_of_birth: profile?.date_of_birth || '',
      gender: profile?.gender || '',
      phone: profile?.phone || '',
      religion: profile?.religion || '',
      caste: profile?.caste || '',
      mother_tongue: profile?.mother_tongue || '',
      height: profile?.height || 0,
      weight: profile?.weight || 0,
      marital_status: profile?.marital_status || '',
      education: profile?.education || '',
      occupation: profile?.occupation || '',
      annual_income: profile?.annual_income || 0,
      location_city: profile?.location_city || '',
      location_state: profile?.location_state || '',
      location_country: profile?.location_country || '',
      about_me: profile?.about_me || '',
      family_details: profile?.family_details || '',
      partner_preferences: profile?.partner_preferences || '',
      profile_photo_url: profile?.profile_photo_url || session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || '',
      gallery_photos: profile?.gallery_photos || [],
      profile_photo_visibility: profile?.profile_photo_visibility || 'everyone',
      hidden_from_users: profile?.hidden_from_users || [],
      is_verified: profile?.is_verified || false,
      is_premium: profile?.is_premium || false,
      profile_views: profile?.profile_views || 0,
      isLoggedIn: true,
      loginTime: new Date().toISOString(),
      sessionExpiry: session.expires_at
    };

    // Update localStorage
    localStorage.setItem('currentUser', JSON.stringify(userData));

    return userData;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const logout = async () => {
  try {
    await supabase.auth.signOut();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('supabaseSession');
    window.location.href = '/';
  } catch (error) {
    console.error('Error logging out:', error);
  }
};

// Check if regular user is authenticated
export const isUserAuthenticated = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch (error) {
    console.error('Error checking user authentication:', error);
    return false;
  }
};

export { supabase };
